import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, AlertTriangle } from "lucide-react";
import MarketingLayout from "../../components/marketing/MarketingLayout";
import Seo from "../../components/Seo";
import AuditForm from "../../components/marketing/AuditForm";
import { SYSTEM_PILLARS, PROCESS, LTC_FAQ } from "../../data/site";

const Section = ({ children, className = "" }) => (
  <section className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 ${className}`}>{children}</section>
);

export default function LeadToClient() {
  return (
    <MarketingLayout>
      <Seo title="Lead-to-Client System — AllToMAIDIt" description="One system from first click to closed customer: capture, CRM, follow-up, attribution and reporting. Get a free Lead System Audit." />

      {/* HERO */}
      <div className="marketing-grid-bg border-b border-white/10">
        <Section className="py-20 lg:py-28">
          <p className="eyebrow text-alto-red mb-5">The AllToMAIDIt Lead-to-Client System</p>
          <h1 className="font-display text-4xl sm:text-6xl font-extrabold leading-[0.95] max-w-3xl">FROM FIRST CLICK TO <span className="text-alto-red">CLOSED CUSTOMER.</span></h1>
          <p className="text-neutral-300 mt-6 max-w-2xl text-sm sm:text-lg leading-relaxed">AllToMAIDIt connects your website, marketing, CRM and follow-up into one customer-acquisition system — so the leads you paid for actually become customers.</p>
          <Link to="/lead-system-audit" data-testid="ltc-hero-cta" className="btn-red font-bold px-7 py-4 rounded-md inline-flex items-center gap-2 mt-8 text-sm">GET YOUR FREE LEAD SYSTEM AUDIT <ArrowRight size={18} /></Link>
        </Section>
      </div>

      {/* PROBLEM + COST */}
      <Section className="py-20">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <p className="eyebrow text-alto-red mb-3">The Problem</p>
            <h2 className="font-display text-3xl font-bold">You're generating leads. Are you keeping them?</h2>
            <p className="text-neutral-400 mt-4 text-sm leading-relaxed">Leads arrive from Google, ads, your website, phone calls, social and referrals — then get scattered, forgotten, or left without a next action. Nobody's sure who followed up, or which campaign actually produced business.</p>
          </div>
          <div className="rounded-2xl p-7 bg-alto-charcoal border border-alto-red/30">
            <div className="flex items-center gap-2 text-alto-red mb-4"><AlertTriangle size={18} /><span className="eyebrow">The Cost of Poor Follow-Up</span></div>
            <ul className="space-y-3">
              {["Money spent on ads that never converts", "Opportunities that go cold within hours", "No attribution — so you can't double down on what works", "A team that assumes someone else handled it"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-neutral-200 text-sm"><span className="w-1.5 h-1.5 bg-alto-red rounded-full mt-2 shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* SYSTEM OVERVIEW */}
      <div className="border-y border-white/10 bg-alto-panel">
        <Section className="py-20">
          <p className="eyebrow text-alto-red mb-3">System Overview</p>
          <h2 className="font-display text-3xl font-bold mb-10">Six connected stages. One system.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SYSTEM_PILLARS.map((p) => (
              <div key={p.key} className="rounded-xl p-6 bg-black border border-white/10">
                <p.icon className="text-alto-red mb-4" size={24} />
                <h3 className="font-display font-extrabold tracking-wide">{p.title}</h3>
                <ul className="mt-3 space-y-1.5">{p.items.map((i) => <li key={i} className="text-sm text-neutral-400">· {i}</li>)}</ul>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* HOW IT WORKS */}
      <Section className="py-20">
        <p className="eyebrow text-alto-red mb-3">How It Works</p>
        <h2 className="font-display text-3xl font-bold mb-10">Audit. Build. Launch. Optimize.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCESS.map((h) => (
            <div key={h.step} className="rounded-xl p-6 bg-alto-charcoal border border-white/10">
              <span className="font-mono text-alto-red text-sm">{h.step}</span><h.icon className="text-white my-4" size={22} />
              <h3 className="font-display font-bold mb-2">{h.title}</h3><p className="text-sm text-neutral-400">{h.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CRM + AUTOMATION + TRACKING */}
      <div className="border-y border-white/10 bg-alto-panel">
        <Section className="py-20 grid md:grid-cols-3 gap-6">
          {[
            { t: "Powered by AllToMAIDIt CRM", d: "Every lead lands in one place with its full timeline, owner and next action — nothing gets lost." },
            { t: "Automated Follow-Up", d: "Tasks, reminders and email/SMS workflows (where configured) keep every opportunity moving." },
            { t: "Source Attribution", d: "UTM + landing page + referrer captured on every lead. Original source is never overwritten." },
          ].map((c) => (
            <div key={c.t} className="rounded-xl p-6 bg-black border border-white/10">
              <h3 className="font-display font-bold text-lg">{c.t}</h3><p className="text-sm text-neutral-400 mt-3">{c.d}</p>
            </div>
          ))}
        </Section>
      </div>

      {/* FAQ */}
      <Section className="py-20">
        <p className="eyebrow text-alto-red mb-3">FAQ</p>
        <h2 className="font-display text-3xl font-bold mb-10">Common questions.</h2>
        <div className="space-y-4 max-w-3xl">
          {LTC_FAQ.map((f) => (
            <div key={f.q} className="rounded-xl p-5 bg-alto-charcoal border border-white/10">
              <h3 className="font-display font-bold flex items-center gap-2"><Check className="text-alto-red" size={16} />{f.q}</h3>
              <p className="text-sm text-neutral-400 mt-2 pl-6">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* AUDIT CTA */}
      <div className="border-t border-white/10 bg-gradient-to-br from-alto-red/15 to-black">
        <Section className="py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold leading-tight">Start with a free Lead System Audit.</h2>
              <p className="text-neutral-300 mt-4 text-sm">See exactly where your opportunities are leaking — and get a plan to fix it.</p>
            </div>
            <AuditForm campaign="Lead-to-Client System" />
          </div>
        </Section>
      </div>
    </MarketingLayout>
  );
}
